import { useEffect, useMemo, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Trend, StatusDot } from "@/components/Scores";
import GettingStarted from "@/components/GettingStarted";
import AthleteLinkCard from "@/components/AthleteLinkCard";
import Tip from "@/components/Tip";
import BulkBar from "@/components/BulkBar";
import SquadStrip from "@/components/SquadStrip";
import { fmtDate } from "@/lib/format";
import { avatarSrc } from "@/components/AthletePhoto";
import { UserPlus, School, Rocket, ChevronDown, ChevronRight, ArrowUp, ArrowDown, ArrowUpRight, Settings2, AlertTriangle, Users2 } from "lucide-react";

const HIGH_RISK = 65;
const COLS_KEY = "xps.cc.cols";

const COLUMNS = [
  { id: "athlete", label: "Athlete", always: true, sort: (a) => a.name?.toLowerCase() },
  { id: "squad", label: "Squad", sort: (a) => (a.squad || "").toLowerCase() },
  { id: "availability", label: "Availability", sort: (a) => a.availability || "" },
  { id: "readiness", label: "Readiness", tip: "How prepared the body is to train hard today, versus this athlete's own baseline.", sort: (a) => a.scores?.readiness ?? -1 },
  { id: "sharpness", label: "Sharpness", tip: "Competition and skill readiness from recent skill work. It decays with rest.", sort: (a) => a.scores?.sharpness ?? -1 },
  { id: "rest", label: "Rest+", tip: "Recovery/sleep score from the athlete's connected wearable (WHOOP recovery, Garmin body-battery, etc.) or their phone.", sort: (a) => a.scores?.rest ?? -1 },
  { id: "effort", label: "Effort+", tip: "Training strain (day strain / activity load) from the wearable, scaled 0-100.", sort: (a) => a.scores?.effort ?? -1 },
  { id: "injury", label: "Injury Risk", tip: "Current model probability of injury. Not a diagnosis. Withheld until there is enough data.", sort: (a) => a.injury_risk ?? -1 },
  { id: "adherence", label: "Adherence 7d", tip: "Share of planned work in the last seven days that the data shows was actually done.", sort: (a) => a.adherence_7d ?? -1 },
  { id: "checkin", label: "Last check-in", tip: "The most recent day we received any real signal · wearable, session or log.", sort: (a) => a.last_data_at || "" },
  { id: "priority", label: "Priority", tip: "Injury risk (primary), recent performance dip, missed check-ins and overdue tasks.", sort: (a) => a.priority ?? -1 },
  { id: "flags", label: "Flags", sort: (a) => (a.injured ? 2 : 0) + (a.setup_pending ? 1 : 0) },
];

const AVAIL_LABEL = { available: "Available", modified: "Modified", unavailable: "Unavailable", at_risk: "At risk" };

function Num({ v, dev }) {
  if (v == null) return <span className="text-muted-foreground">-</span>;
  return (
    <span className="inline-flex items-baseline gap-1.5">
      <span className="font-heading font-bold tabular-nums">{v}</span>
      {dev && <span className="text-[10px]"><Trend dev={dev} /></span>}
    </span>
  );
}

export default function CommandCenter({ embedded, squadFilter, academyId="" } = {}) {
  const [athletes, setAthletes] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTutorial, setShowTutorial] = useState(false);
  const [sort, setSort] = useState({ id: "priority", dir: "desc" });
  const [collapsed, setCollapsed] = useState({});
  const [colsOpen, setColsOpen] = useState(false);
  const [sel, setSel] = useState([]);
  const [hidden, setHidden] = useState(() => {
    try { return JSON.parse(localStorage.getItem(COLS_KEY) || "[]"); } catch { return []; }
  });
  const { user, checkAuth } = useAuth();
  const isStaff = user?.role !== "athlete";
  const isAdmin = ["admin", "owner"].includes(user?.role);
  // Non-admin staff (coach/physio/snc/nutritionist/doctor) default to "my squad only".
  const [scope, setScope] = useState(isStaff && !isAdmin ? "mine" : "all");
  const navigate = useNavigate();

  useEffect(() => {
    const q = `?scope=${scope}`;
    Promise.all([api.get(`/athletes${q}`), embedded ? Promise.resolve({data:null}) : api.get(`/dashboard/summary${q}`)])
      .then(([a, s]) => {
        setAthletes(a.data.filter(row=>(!squadFilter || (row.teams || [row.sub_team || row.squad || row.sport]).includes(squadFilter)) && (!academyId || (row.academy_id || '__unaffiliated__')===academyId))); setSummary(s.data);
        if (!embedded && a.data.length === 0 && !user?.tutorial_done) setShowTutorial(true);
      })
      .finally(() => setLoading(false));
  }, [user, scope, squadFilter, academyId, embedded]);

  useEffect(() => { localStorage.setItem(COLS_KEY, JSON.stringify(hidden)); }, [hidden]);
  const [legend, setLegend] = useState(false);

  const closeTutorial = async () => {
    setShowTutorial(false);
    try { await api.post("/me/tutorial-complete"); checkAuth(); } catch { /* noop */ }
  };

  const reload = () => api.get(`/athletes?scope=${scope}`).then((r) => setAthletes(r.data.filter(a=>(!squadFilter || (a.teams || [a.sub_team || a.squad || a.sport]).includes(squadFilter)) && (!academyId || (a.academy_id || '__unaffiliated__')===academyId)))).catch(() => {});

  const cols = COLUMNS.filter((c) => c.always || !hidden.includes(c.id));

  const groups = useMemo(() => {
    const cmp = COLUMNS.find((c) => c.id === sort.id) || COLUMNS[COLUMNS.length - 2];
    const dir = sort.dir === "asc" ? 1 : -1;
    const by = {};
    athletes.forEach((a) => { for (const name of squadFilter ? [squadFilter] : a.teams || [a.sub_team || a.squad || a.sport || "Unassigned"]) (by[name] ||= []).push(a); });
    return Object.entries(by)
      .map(([squad, rows]) => {
        const sorted = [...rows].sort((x, y) => {
          const vx = cmp.sort(x), vy = cmp.sort(y);
          if (vx === vy) return 0;
          return (vx > vy ? 1 : -1) * dir;
        });
        const nums = rows.map((r) => r.scores?.readiness).filter((n) => n != null);
        return {
          squad, rows: sorted,
          avgReadiness: nums.length ? Math.round(nums.reduce((s, n) => s + n, 0) / nums.length) : null,
          highRisk: rows.filter((r) => (r.injury_risk ?? 0) >= HIGH_RISK).length,
          unavailable: rows.filter((r) => r.availability === "unavailable").length,
        };
      })
      .filter((g) => !squadFilter || g.squad === squadFilter)
      .sort((a, b) => a.squad.localeCompare(b.squad));
  }, [athletes, sort, squadFilter]);

  const toggleSort = (id) => setSort((s) => (s.id === id ? { id, dir: s.dir === "desc" ? "asc" : "desc" } : { id, dir: "desc" }));

  return (
    <div>
      <header className={`min-h-16 border-b border-border flex flex-wrap items-center gap-3 px-4 sm:px-8 py-3 bg-background ${embedded ? "" : "sticky top-0 z-10"}`}>
        {!embedded && <h1 className="font-heading text-lg sm:text-xl font-black tracking-tight">Command Center</h1>}
        <span className="hidden sm:inline text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">
          {squadFilter || "Population"} · every athlete you coach
        </span>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {isStaff && !isAdmin && !embedded && (
            <div className="flex items-center rounded-full border border-border overflow-hidden text-xs" data-testid="squad-scope-toggle">
              <button data-testid="scope-mine" onClick={() => setScope("mine")}
                className={`px-3 py-1.5 flex items-center gap-1.5 ${scope === "mine" ? "bg-foreground text-background" : "hover:bg-surface"}`}>
                <Users2 className="w-3.5 h-3.5" /> My squad{summary?.my_squad_size ? ` · ${summary.my_squad_size}` : ""}
              </button>
              <button data-testid="scope-all" onClick={() => setScope("all")}
                className={`px-3 py-1.5 ${scope === "all" ? "bg-foreground text-background" : "hover:bg-surface"}`}>
                See all
              </button>
            </div>
          )}
          <div className="relative">
            <button onClick={() => setLegend((v) => !v)} data-testid="legend-btn"
              className="text-xs border border-border px-3 py-2 hover:bg-surface transition-colors">
              What do these mean?
            </button>
            <button onClick={() => setColsOpen((v) => !v)} data-testid="columns-btn"
              className="text-xs border border-border px-2.5 py-1.5 hover:bg-surface transition-colors flex items-center gap-1.5">
              <Settings2 className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Columns</span>
            </button>
            {colsOpen && (
              <div className="absolute right-0 top-full mt-1 z-30 w-52 border border-border bg-background shadow-lg p-2" data-testid="columns-menu">
                {COLUMNS.filter((c) => !c.always).map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-xs py-1.5 px-1 cursor-pointer hover:bg-surface">
                    <input type="checkbox" data-testid={`col-toggle-${c.id}`} checked={!hidden.includes(c.id)}
                      onChange={(e) => setHidden((h) => (e.target.checked ? h.filter((x) => x !== c.id) : [...h, c.id]))} />
                    {c.label}
                  </label>
                ))}
              </div>
            )}
          </div>
          <button onClick={() => setShowTutorial(true)} data-testid="tutorial-open-btn"
            className="text-xs border border-border px-2.5 py-1.5 hover:bg-surface transition-colors flex items-center gap-1.5">
            <Rocket className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Getting started</span>
          </button>
        </div>
      </header>

      {legend && (
        <div className="border-b border-border bg-surface px-4 sm:px-8 py-4" data-testid="legend-panel">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-3">What each column means</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
            {COLUMNS.filter((c) => c.tip).map((c) => (
              <div key={c.id} data-testid={`legend-${c.id}`}>
                <span className="text-sm font-bold">{c.label}</span>
                <p className="text-[12px] text-muted-foreground leading-snug">{c.tip}</p>
              </div>
            ))}
          </div>
          <button onClick={() => setLegend(false)} className="mt-3 text-xs border border-border px-3 py-1.5 hover:bg-background transition-colors">Close</button>
        </div>
      )}

      <GettingStarted open={showTutorial} onClose={closeTutorial} />

      {!embedded && <div className="grid grid-cols-2 lg:grid-cols-4 border-b border-border">
        {[
          { k: "Data Validity", v: summary?.avg_validity ?? "-", suffix: summary?.avg_validity != null ? "/100" : "", c: summary?.avg_validity >= 75 ? "text-traffic-green" : summary?.avg_validity >= 55 ? "" : "text-traffic-red", testid: "kpi-validity", tip: "Squad-average walk-forward forecast skill. It rises as real data replaces model priors." },
          { k: "Athletes", v: summary?.total ?? "-", testid: "kpi-athletes" },
          { k: "Above Baseline", v: summary?.above_baseline ?? "-", c: "text-traffic-green", testid: "kpi-above-baseline", tip: "Athletes currently above their own baseline on the majority of scores." },
          { k: "Avg Adherence · 7d", v: summary?.avg_adherence_7d != null ? summary.avg_adherence_7d : "-", suffix: summary?.avg_adherence_7d != null ? "%" : "", testid: "kpi-adherence" },
        ].map((s, i) => (
          <div key={s.k} className={`p-4 sm:p-6 ${i < 3 ? "border-r border-border" : ""} ${i < 2 ? "border-b lg:border-b-0 border-border" : ""}`} data-testid={s.testid || `kpi-${i}`}>
            <div className="text-[10px] sm:text-xs tracking-[0.18em] uppercase font-bold text-muted-foreground flex items-center gap-1.5">
              {s.k}{s.tip && <Tip text={s.tip} label={s.k} />}
            </div>
            <div className={`font-heading text-3xl sm:text-4xl font-black tabular-nums mt-1.5 ${s.c || ""}`}>
              {s.v}<span className="text-base text-muted-foreground">{s.suffix || ""}</span>
            </div>
          </div>
        ))}
      </div>}

      {isStaff && !embedded && athletes.length > 0 && (
        <SquadStrip athletes={athletes} squads={groups.map((g) => g.squad)} onReload={reload} />
      )}

      {isStaff && <BulkBar selected={sel} athletes={athletes} onClear={() => setSel([])} onDone={() => { setSel([]); reload(); }} />}

      {loading ? (
        <div className="p-8 text-sm text-muted-foreground">Loading squad…</div>
      ) : athletes.length === 0 ? (
        <div className="p-8 sm:p-16 flex flex-col items-center text-center" data-testid="athletes-empty">
          {user?.role === "athlete" ? (
            <AthleteLinkCard onLinked={() => window.location.reload()} />
          ) : (
            <>
              <div className="w-14 h-14 rounded-full bg-surface border border-border flex items-center justify-center mb-5">
                <UserPlus className="w-6 h-6 text-muted-foreground" />
              </div>
              <h2 className="font-heading text-2xl font-black tracking-tight">No athletes yet</h2>
              <p className="text-sm text-muted-foreground mt-2 max-w-md">Add your first athlete or create an academy and invite your team. Your athletes and their digital twins will appear here.</p>
              <div className="flex flex-wrap gap-3 mt-6 justify-center">
                <button onClick={() => navigate("/add-athlete")} data-testid="empty-add-athlete"
                  className="text-sm px-4 py-2 bg-foreground text-background hover:bg-neutral-800 transition-colors flex items-center gap-1.5">
                  <UserPlus className="w-4 h-4" /> Add athlete
                </button>
                <button onClick={() => navigate("/academy")} data-testid="empty-create-academy"
                  className="text-sm px-4 py-2 border border-border hover:bg-surface transition-colors flex items-center gap-1.5">
                  <School className="w-4 h-4" /> Create academy
                </button>
                <button onClick={() => setShowTutorial(true)} data-testid="empty-tutorial"
                  className="text-sm px-4 py-2 border border-border hover:bg-surface transition-colors flex items-center gap-1.5">
                  <Rocket className="w-4 h-4" /> Getting started
                </button>
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="scroll-x border-b border-border">
          <table className="w-full text-sm border-collapse" data-testid="athlete-table">
            <thead>
              <tr className="border-b border-border bg-surface">
                {isStaff && (
                  <th className="px-2 py-2 w-8">
                    <input type="checkbox" data-testid="select-all" aria-label="Select all athletes"
                      checked={sel.length > 0 && sel.length === athletes.length}
                      onChange={(e) => setSel(e.target.checked ? athletes.map((a) => a.athlete_id) : [])} />
                  </th>
                )}
                {cols.map((c) => (
                  <th key={c.id} data-testid={`th-${c.id}`}
                    className={`text-left px-3 py-2 text-[10px] tracking-[0.14em] uppercase font-bold text-muted-foreground whitespace-nowrap ${c.id === "athlete" ? "min-w-[190px]" : ""}`}>
                    <button onClick={() => toggleSort(c.id)} className="inline-flex items-center gap-1 hover:text-foreground transition-colors">
                      {c.label}
                      {sort.id === c.id && (sort.dir === "desc" ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />)}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <Fragment key={g.squad}>
                  <tr className="bg-surface/60 border-b border-border" data-testid={`squad-group-${g.squad}`}>
                    <td colSpan={cols.length + (isStaff ? 1 : 0)} className="px-3 py-1.5">
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 w-full">
                        <button onClick={() => setCollapsed((c) => ({ ...c, [g.squad]: !c[g.squad] }))}
                          data-testid={`squad-toggle-${g.squad}`}
                          className="flex flex-wrap items-center gap-x-4 gap-y-1 text-left flex-1">
                          {collapsed[g.squad] ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                          <span className="font-heading font-bold tracking-tight">{g.squad}</span>
                          <span className="text-[11px] text-muted-foreground">{g.rows.length} athlete{g.rows.length === 1 ? "" : "s"}</span>
                          {g.avgReadiness != null && <span className="text-[11px] text-muted-foreground">avg readiness <b className="text-foreground tabular-nums">{g.avgReadiness}</b></span>}
                          {g.highRisk > 0 && <span className="text-[11px] text-traffic-red">{g.highRisk} high injury risk</span>}
                          {g.unavailable > 0 && <span className="text-[11px] text-muted-foreground">{g.unavailable} unavailable</span>}
                        </button>
                        {isStaff && (
                          <button onClick={(e) => { e.stopPropagation(); navigate(`/?tab=simulator&squad=${encodeURIComponent(g.squad)}`); }}
                            data-testid={`squad-open-${g.squad}`}
                            className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-foreground text-background px-3 py-1.5 hover:bg-neutral-800 transition-colors">
                            <Users2 className="w-3.5 h-3.5" /> Open Squad Simulator
                            <ArrowUpRight className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {!collapsed[g.squad] && g.rows.map((a) => {
                    const high = (a.injury_risk ?? 0) >= HIGH_RISK;
                    return (
                      <tr key={a.athlete_id} data-testid={`athlete-row-${a.athlete_id}`}
                        onClick={() => navigate(`/athlete/${a.athlete_id}`)}
                        className={`border-b border-border cursor-pointer hover:bg-surface transition-colors ${high ? "bg-red-500/[0.06] border-l-2 border-l-red-500/40" : "border-l-2 border-l-transparent"} ${a.availability === "unavailable" ? "opacity-60" : ""}`}>
                        {isStaff && (
                          <td className="px-2 py-1.5 w-8" onClick={(e) => e.stopPropagation()}>
                            <input type="checkbox" data-testid={`select-${a.athlete_id}`} checked={sel.includes(a.athlete_id)}
                              onChange={(e) => setSel((p) => e.target.checked ? [...p, a.athlete_id] : p.filter((x) => x !== a.athlete_id))} />
                          </td>
                        )}
                        {cols.map((c) => (
                          <td key={c.id} className="px-3 py-1.5 whitespace-nowrap align-middle">
                            {c.id === "athlete" && (
                              <span className="flex items-center gap-2 min-w-0">
                                <img src={avatarSrc(a)}
                                  alt="" className="w-6 h-6 grayscale object-cover border border-border shrink-0" />
                                <StatusDot status={a.overall_status} />
                                <span className="font-medium truncate max-w-[150px]">{a.name}</span>
                              </span>
                            )}
                            {c.id === "squad" && <span className="text-muted-foreground">{a.squad}</span>}
                            {c.id === "availability" && (
                              <span className={a.availability === "unavailable" ? "text-traffic-red" : a.availability === "modified" ? "text-traffic-amber" : "text-muted-foreground"}
                                data-testid={`avail-${a.athlete_id}`}>
                                {AVAIL_LABEL[a.availability] || "Available"}
                              </span>
                            )}
                            {c.id === "readiness" && <Num v={a.setup_pending ? null : a.scores?.readiness} dev={a.status?.readiness} />}
                            {c.id === "sharpness" && <Num v={a.setup_pending ? null : a.scores?.sharpness} dev={a.status?.sharpness} />}
                            {c.id === "injury" && (
                              a.injury_risk == null
                                ? <span className="text-muted-foreground text-xs" data-testid={`injury-pending-${a.athlete_id}`}>Awaiting data</span>
                                : <span className={`font-heading font-bold tabular-nums ${high ? "text-traffic-red" : ""}`} data-testid={`injury-${a.athlete_id}`}>{a.injury_risk}</span>
                            )}
                            {c.id === "rest" && <Num v={a.setup_pending ? null : a.scores?.rest} dev={a.status?.rest} />}
                            {c.id === "effort" && <Num v={a.setup_pending ? null : a.scores?.effort} dev={a.status?.effort} />}
                            {c.id === "adherence" && (a.adherence_7d == null ? <span className="text-muted-foreground">-</span> : <span className="tabular-nums">{a.adherence_7d}%</span>)}
                            {c.id === "checkin" && (
                              <span className="text-muted-foreground text-xs">
                                {a.last_data_at ? fmtDate(a.last_data_at) : "-"}
                                {a.days_stale > 2 && <span className="text-traffic-amber"> · {a.days_stale}d</span>}
                              </span>
                            )}
                            {c.id === "priority" && <span className="font-heading font-bold tabular-nums" data-testid={`priority-${a.athlete_id}`}>{a.priority}</span>}
                            {c.id === "flags" && (
                              <span className="flex items-center gap-1.5">
                                {a.injured && <span title="Active moderate/severe injury" className="text-traffic-red"><AlertTriangle className="w-3.5 h-3.5" /></span>}
                                {a.setup_pending && (
                                  <button data-testid={`pending-${a.athlete_id}`}
                                    onClick={(e) => { e.stopPropagation(); navigate(`/athlete/${a.athlete_id}?tab=performance&wearables=1`); }}
                                    title="No device connected yet · tap to connect a wearable"
                                    className="text-[10px] uppercase tracking-wider border border-border px-1.5 py-0.5 text-muted-foreground hover:bg-surface hover:text-foreground transition-colors">
                                    Connect device
                                  </button>
                                )}
                              </span>
                            )}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
